import { fetchQuestById } from '@/lib/questService';
import { ElementType, Chapter, Quest, Lesson, InstructorProfile, RawInstructorProfile, RawQuestsData, RawChapter, RawLesson, RawQuest, Element, Extracurricular, ExtracurricularSchema, ModeType, Profile } from '@/constant/schema';
import supabase from './supabase';
import { extractYouTubeVideoId, isLearnTest } from '@/lib/utils';
import { getQuestDistricts, getQuestGrades, normalizeGrade, getNextGrade, matchQuestByDistrictAndGrade, normalizeSchoolDistrict } from '@/lib/schoolDistrictGradeUtils';


const transformInstructorProfile = (rawInstructorProfile: RawInstructorProfile, id: string): InstructorProfile => {
  return {
    ...rawInstructorProfile,
    id: id,
    name: rawInstructorProfile.name || "",
    avatar_url: rawInstructorProfile.avatar_url || "",
    full_body_url: rawInstructorProfile.full_body_url || "",
    greeting: rawInstructorProfile.greeting || "",
  }
}

const transformQuest = (rawQuest: RawQuest): Quest => {
  return {
    ...rawQuest,
    id: rawQuest.id,
    title: rawQuest.title,
    description: rawQuest.description,
    book_name: rawQuest.book_name || "",
    instructor_profile: transformInstructorProfile(rawQuest.instructor_profile, rawQuest.id) || null,
    cover_image: rawQuest.cover_image || "",
    primary_color_theme: rawQuest.primary_color_theme,
    secondary_color_theme: rawQuest.secondary_color_theme,
    chapters: [],
    lock: rawQuest.lock,
    subject: rawQuest.subject || "",
    series: rawQuest.series || "",
    tab_config: rawQuest.tab_config || null,
    default_grade: rawQuest.default_grade || null,
    school_district_grade_mapping: rawQuest.school_district_grade_mapping || null,
  }
}

const transformChapter = (rawChapter: RawChapter): Chapter => {
  return {
    ...rawChapter,
    id: rawChapter.id,
    title: rawChapter.title,
    visible: rawChapter.visible,
    lesson_list: [],
    order: rawChapter.order,
    questions: rawChapter.questions || [],
    unit: rawChapter.unit || "",
  }
}

const transformLesson = (rawLesson: RawLesson): Lesson => {
  return {
    ...rawLesson,
    id: rawLesson.id,
    title: rawLesson.title,
    description: rawLesson.description || "",
    keypoints: rawLesson.keypoints || [],
    welcome_message: rawLesson.welcome_message || "",
    lesson_prefix: rawLesson.lesson_prefix || "",
    questions: rawLesson.questions || [],
    elements: [],
  }
}

const transformElement = (rawElement: any): any => {
  return {
    id: rawElement.id,
    lesson_id: rawElement.lesson_id,
    element_type: transformElementType(rawElement.element_type),
    title: rawElement.title,
  }
}

const transformElementType = (rawElementType: string): ElementType => {
  switch (rawElementType) {
    case 'interactive':
      return ElementType.INTERACTIVE;
    case 'timeline':
      return ElementType.TIMELINE;
    case 'drag_and_drop':
      return ElementType.DRAG_AND_DROP;
    case 'word_search':
      return ElementType.WORD_SEARCH;
    case 'flashcard':
      return ElementType.FLASH_CARD;
    case 'lesson_summary':
      return ElementType.LESSON_SUMMARY;
    case 'recommend_videos':
      return ElementType.RECOMMEND_VIDEOS;
    case 'recommend_shorts':
      return ElementType.RECOMMEND_SHORTS;
    default:
      // 默认值处理
      return ElementType.RECOMMEND_VIDEOS;
  }
}

const transformQuests = (rawData: RawQuestsData): { quests: Quest[], questMap: Record<string, Quest>, chapterMap: Record<string, Chapter>, lessonMap: Record<string, Lesson>, elementMap: Record<string, Element> } => {
  const quests: Quest[] = [];

  const questMap: Record<string, Quest> = {};
  const chapterMap: Record<string, Chapter> = {};
  const lessonMap: Record<string, Lesson> = {};
  const elementMap: Record<string, Element> = {};

  rawData.quests.map((quest) => {
    questMap[quest.id] = transformQuest(quest);
    quests.push(questMap[quest.id])
  })

  rawData.chapters.map((chapter) => {
    chapterMap[chapter.id] = transformChapter(chapter)
    questMap[chapter.quest_id]?.chapters.push(chapterMap[chapter.id])
  })

  rawData.lessons.map((lesson) => {
    lessonMap[lesson.id] = transformLesson(lesson)
    chapterMap[lesson.chapter_id]?.lesson_list.push(lessonMap[lesson.id])
  })

  rawData.elements.map((element) => {
    elementMap[element.id] = transformElement(element)
    lessonMap[element.lesson_id]?.elements.push(elementMap[element.id])
  })

  return { quests, questMap, chapterMap, lessonMap, elementMap };
}

/**
 * 获取quest的lesson和chapter的mode，用于完成任务的，排除play和challenge
 * @param quest 
 * @returns lessonModes: ModeType[], chapterModes: ModeType[]
 */
const getQuestModes = (quest: Quest): { lessonModes: ModeType[], chapterModes: ModeType[] } => {
  const lessonModes = []
  const chapterModes = []
  quest?.tab_config?.showLearn && lessonModes.push(ModeType.LEARN)
  quest?.tab_config?.showPractice && lessonModes.push(ModeType.PRACTICE)
  // quest?.tab_config?.showChallenge && lessonModes.push(ModeType.CHALLENGE)
  // quest?.tab_config?.showPlay && lessonModes.push(ModeType.PLAY)
  quest?.tab_config?.showLearn && chapterModes.push(ModeType.TEST)
  return { lessonModes, chapterModes };
}

/**
 * 获取所有书籍的基本信息，不包括章节和课程
 * 这个函数比 getAllQuests 更高效，因为它只获取书籍的基本信息
 */
export const getQuestsList = async (): Promise<Quest[]> => {
  const { data, error } = await supabase
    .from('syllabus_quest')
    .select('id, title, book_name, primary_color_theme, secondary_color_theme, description, instructor_profile, cover_image, created_at, lock, subject, tab_config, visible, school_district_grade_mapping')
    .order('order');

  if (error) {
    console.error('Error fetching quests list:', error);
    throw error;
  }

  // 过滤条件：只显示可见且未锁定的quest
  const filterQuests = data.filter((quest: any) => quest.visible && quest.lock === false) as Quest[];
  // 将数据转换为 Quest 对象
  const quests: Quest[] = filterQuests.map((rawQuest: any) => ({
    ...rawQuest,
    id: rawQuest.id,
    created_at: rawQuest.created_at,
    title: rawQuest.title,
    description: rawQuest.description,
    book_name: rawQuest.book_name || "",
    instructor_profile: transformInstructorProfile(rawQuest.instructor_profile || {}, rawQuest.id),
    cover_image: rawQuest.cover_image || "",
    primary_color_theme: rawQuest.primary_color_theme,
    secondary_color_theme: rawQuest.secondary_color_theme,
    chapters: [], // 空章节列表
    lock: rawQuest.lock || false,
    subject: rawQuest.subject || "",
    tab_config: rawQuest.tab_config || null,
    school_district_grade_mapping: rawQuest.school_district_grade_mapping || null,
  }));

  return quests;
};

/**
 * 获取单个书籍的完整信息，包括章节和课程
 * @param questId 书籍ID
 */
export const getQuestWithChapters = async (questId: string): Promise<Quest> => {
  // 1. 获取书籍基本信息
  const { data: questData, error: questError } = await supabase
    .from('syllabus_quest')
    .select('*')
    .eq('id', questId)
    .single();

  if (questError) {
    console.error('Error fetching quest:', questError);
    throw questError;
  }

  const quest = transformQuest(questData);

  // 2. 获取章节信息 (只获取可见的章节)
  const { data: chapters, error: chaptersError } = await supabase
    .from('syllabus_chapter')
    .select('*')
    .eq('quest_id', questId)
    .eq('visible', true)
    .order('order');

  if (chaptersError) {
    console.error('Error fetching chapters:', chaptersError);
    throw chaptersError;
  }

  // 3. 获取所有章节的课程信息
  const chapterIds = chapters.map(chapter => chapter.id);
  const { data: lessons, error: lessonsError } = await supabase
    .from('syllabus_lesson')
    .select('*')
    .in('chapter_id', chapterIds)
    .order('order');

  if (lessonsError) {
    console.error('Error fetching lessons:', lessonsError);
    throw lessonsError;
  }

  // 4. 组装数据
  const chaptersMap: Record<string, Chapter> = {};
  chapters.forEach(chapter => {
    chaptersMap[chapter.id] = transformChapter(chapter);
  });

  lessons.forEach(lesson => {
    const transformedLesson = transformLesson(lesson);
    if (chaptersMap[lesson.chapter_id]) {
      chaptersMap[lesson.chapter_id].lesson_list.push(transformedLesson);
    }
  });

  quest.chapters = Object.values(chaptersMap);

  return quest;
};

// Quests with brief elements
export const getAllQuests = async (onlyVisible: boolean = true): Promise<Quest[]> => {
  // const latestUpdateTime = await getLatestUpdateTime();
  // if (localStorage.getItem('allQuestsWithBrief') && localStorage.getItem('latestUpdateTime') && latestUpdateTime <= parseInt(localStorage.getItem('latestUpdateTime') || '0')) {
  //   return JSON.parse(localStorage.getItem('allQuestsWithBrief') || '{}');
  // }

  const { data, error } = await supabase.rpc('get_quests_with_chapters_and_lessons_2');
  if (error) {
    console.error('Error fetching all quests:', error);
    throw error;
  }
  // PostgreSQL 函数直接返回数组，不需要访问 .quests 属性
  let quests
  if (onlyVisible) {
    quests = data.filter((quest: any) => quest.visible && quest.lock === false) as Quest[];
  } else {
    quests = data as Quest[];
  }
  if (isLearnTest && onlyVisible) {
    // 如果data中有book_name===TestBook，则加入quests
    const testBook = data.find((quest: any) => quest.book_name === 'TestBook');
    if (testBook) {
      quests.push(testBook);
    }
  }
  // localStorage.setItem('allQuestsWithBrief', JSON.stringify(quests));
  // localStorage.setItem('latestUpdateTime', latestUpdateTime.toString());
  return quests;
}

export const getQuestById = async (questId: string): Promise<Quest | undefined> => {
  const quest = await fetchQuestById(questId);
  // conver RawQuest to Quest.
  return transformQuest(quest);
}

// 保存lesson_map会导致过大，直接从allQuests中获取
export const getLessonById = async (lessonId: string): Promise<Lesson | undefined> => {
  // 遍历allQuests, 返回lessonId相同的lesson
  const allQuests = await getAllQuests();
  for (const quest of allQuests) {
    for (const chapter of quest.chapters) {
      for (const lesson of chapter.lesson_list) {
        if (lesson.id === lessonId) {
          return lesson;
        }
      }
    }
  }
  return undefined;
};

// 帮助生成欢迎消息的工具函数
const getWelcomeMessage = async (questId: string): Promise<string> => {
  const questData = await getQuestById(questId);
  if (questData) {
    return questData.instructor_profile.greeting ||
      `I'm ${questData.instructor_profile.name}, and I'll be your guide for this course on ${questData.title}.`;
  } else {
    return "Welcome to your learning journey!";
  }
};

// Game Center types
export interface ExternalGame {
  id: number;
  name: string;
  description: string | null;
  logo: string | null;
  url: string;
  click: number | null;
  play_time: number | null;
  recommend: boolean;
  created_at?: string;
  visible?: boolean;
}

export interface Playground {
  id: number;
  created_at: string;
  title: string;
  url: string;
  cover: string | null;
  type?: string;
  user_id?: string;
}

export interface GameCenterData {
  games: ExternalGame[];
  featuredPlaygrounds: Playground[];
}

/**
 * Get all data needed for GameCenter (public data only, no user-specific data)
 */
export const getGameCenterData = async (): Promise<GameCenterData> => {
  // Fetch games
  const { data: gamesData, error: gamesError } = await supabase
    .from('external_game')
    .select('*')
    .eq('visible', true);

  if (gamesError) {
    console.error('Error fetching games:', gamesError);
    throw gamesError;
  }

  // Fetch featured playgrounds
  const { data: featuredData, error: featuredError } = await supabase
    .from('playgrounds')
    .select('*')
    .eq('type', 'featured')
    .order('created_at', { ascending: false });

  if (featuredError) {
    console.error('Error fetching featured playgrounds:', featuredError);
    throw featuredError;
  }

  return {
    games: gamesData || [],
    featuredPlaygrounds: featuredData || []
  };
};

export const getExtracurriculars = async (): Promise<Extracurricular[]> => {
  // 根据created_at，倒叙排列
  const { data, error } = await supabase.from('video_playlist').select('*').order('created_at', { ascending: false });
  if (error) {
    console.error('Error fetching extracurriculars:', error);
    throw error;
  }

  const extracurriculars = data.map((rawExtracurricular: Extracurricular) => ExtracurricularSchema.parse(rawExtracurricular));

  // 获取所有视频ID
  const allYoutubeIds: string[] = [];
  const playlistYoutubeIdsMap = new Map<string, string[]>();

  extracurriculars.forEach((extracurricular) => {
    const youtubeIds = extracurricular.video_url_list
      .map((url: string) => {
        try {
          return extractYouTubeVideoId(url);
        } catch (error) {
          console.warn('Failed to extract YouTube ID from URL:', url, error);
          return null;
        }
      })
      .filter((id): id is string => id !== null);

    playlistYoutubeIdsMap.set(extracurricular.id, youtubeIds);
    allYoutubeIds.push(...youtubeIds);
  });

  // 一次性获取所有YouTube缓存数据
  let youtubeCacheData: any[] = [];
  if (allYoutubeIds.length > 0) {
    // 只需要获取questions这个数组的长度
    const { data: cacheData, error: cacheError } = await supabase
      .from('youtube_cache')
      .select('youtube_id, questions, questions_visible')
      .in('youtube_id', allYoutubeIds)

    if (cacheError) {
      console.error('YouTube cache error:', cacheError);
    } else {
      youtubeCacheData = cacheData || [];
    }
  }

  // 创建YouTube ID到缓存数据的映射
  const youtubeCacheMap = new Map<string, any>();
  youtubeCacheData.forEach((item) => {
    youtubeCacheMap.set(item.youtube_id, item);
  });

  // 处理结果
  const extracurricularsWithCounts = extracurriculars.map((extracurricular) => {
    const playlistYoutubeIds = playlistYoutubeIdsMap.get(extracurricular.id) || [];
    const playlistCacheData = playlistYoutubeIds
      .map(id => youtubeCacheMap.get(id))
      .filter(item => item !== undefined);

    const videoCount = playlistCacheData.length;
    const quizCount = playlistCacheData.reduce((total: number, item: any) => {
      return total + (item.questions?.length || 0);
    }, 0);

    return {
      ...extracurricular,
      videoCount,
      quizCount
    };
  });

  return extracurricularsWithCounts;
}

// 检查是否匹配用户所在学区
const isUserSchoolMatch = (quest: Quest, userDistrict: string): boolean => {
  const questSchools = getQuestDistricts(quest);
  if (!userDistrict || questSchools.length === 0) {
    return false;
  }

  const normalizedUserDistrict = userDistrict.toLowerCase().trim();

  return questSchools.some((school: string) => {
    const normalizedSchool = school.toLowerCase().trim();
    return normalizedSchool === normalizedUserDistrict ||
      normalizedSchool.includes(normalizedUserDistrict) ||
      normalizedUserDistrict.includes(normalizedSchool);
  });
};

// Quest 排序结果类型，包含标签信息
export type QuestWithLabel = Quest & {
  label: {
    type: 'user_district' | 'premium_school' | 'other';
    schoolNames?: string[]; // 如果是 premium_school，包含学校名称数组（可能包含 Challenger 和/或 Basis）
  };
};

// 排序选项类型
export interface QuestSortOptions {
  userDistrict?: string;      // 用户学区，如果提供且非空则优先排序命中的书籍
  userGrade?: string;          // 用户年级，如果提供且非空则优先排当前和高一级年级
  bookshelfQuestIds?: string[]; // 书架上的书籍ID列表，如果提供则不在书架的优先
  allProgress?: any[];         // 学习进度数据，如果提供则最近学习的优先
}

/**
 * 根据用户的 profile 和 quest 信息对 quest 进行排序（通用版本）
 *
 * 排序优先级（按顺序，仅在提供相应参数时生效）：
 * 1. 书架状态（如果提供 bookshelfQuestIds）：不在书架上的书籍优先
 * 2. 锁定状态：未锁定的书籍优先
 * 3. 学习进度（如果提供 allProgress）：最近学习的优先
 * 4. 用户所在学区（如果提供 userDistrict）：命中用户学区的书籍优先
 * 5. Premium 学校：Challenger 和 BASIS 学区的书籍次之
 * 6. 系列排序：同一系列的书放在一起，不同系列按字母排序，无系列的书排在最后
 * 7. 年级匹配（如果提供 userGrade）：同一系列内按年级排序，当前年级 > 高一级 > 其他年级
 *
 * 每个 quest 会被添加一个 label 字段：
 * - type: 'user_district' - 命中用户所在学区
 * - type: 'premium_school' - 命中高级学校（Challenger 和/或 Basis），schoolNames 包含学校名称数组
 * - type: 'other' - 其他书籍
 *
 * 使用示例：
 * ```
 * // 基础用法（只考虑学区和年级）
 * const sorted = sortQuestsByUserProfile(quests, { userDistrict, userGrade });
 *
 * // 完整用法（包含书架和进度）
 * const sorted = sortQuestsByUserProfile(quests, {
 *   userDistrict,
 *   userGrade,
 *   bookshelfQuestIds,
 *   allProgress
 * });
 * ```
 *
 * @param quests - 需要排序的 Quest 数组
 * @param options - 排序选项（所有参数都是可选的）
 * @returns 排序后的带 label 的 Quest 数组
 */
export const sortQuestsByUserProfile = (
  quests: Quest[],
  options: QuestSortOptions = {}
): QuestWithLabel[] => {
  const {
    userDistrict,
    userGrade,
    bookshelfQuestIds,
    allProgress
  } = options;
  if (quests === null || quests.length === 0) {
    return [];
  }
  // 为每个 quest 添加 label
  const questsWithLabels: QuestWithLabel[] = quests.map(quest => {
    const questSchools = getQuestDistricts(quest);

    // 检查是否为高级学校，并返回所有匹配的学校名称
    const getPremiumSchoolNames = (): string[] => {
      if (questSchools.length === 0) {
        return [];
      }

      const premiumSchools: string[] = [];

      const hasChallenger = questSchools.some((school: string) => {
        return school.toLowerCase().includes('challenger');
      });

      if (hasChallenger) {
        premiumSchools.push('Challenger');
      }

      const hasBasis = questSchools.some((school: string) => {
        return school.toLowerCase().includes('basis');
      });

      if (hasBasis) {
        premiumSchools.push('BASIS');
      }

      return premiumSchools;
    };

    // 确定 label
    let label: QuestWithLabel['label'];

    if (isUserSchoolMatch(quest, userDistrict || '')) {
      label = { type: 'user_district' };
    } else {
      const premiumSchoolNames = getPremiumSchoolNames();
      if (premiumSchoolNames.length > 0) {
        label = { type: 'premium_school', schoolNames: premiumSchoolNames };
      } else {
        label = { type: 'other' };
      }
    }

    return {
      ...quest,
      label
    };
  });

  // 辅助函数：获取最新的学习进度
  const getLatestProgress = (quest: Quest) => {
    if (!allProgress) return null;

    return allProgress.reduce((latest, current) => {
      if (current.quest_id !== quest.id) return latest;
      if (!latest) return current;

      const currentTime = current.updated_at ? new Date(current.updated_at).getTime() : 0;
      const latestTime = latest.updated_at ? new Date(latest.updated_at).getTime() : 0;

      return currentTime > latestTime ? current : latest;
    }, null as any);
  };

  // 辅助函数：计算年级匹配分数
  const getGradeMatchScore = (quest: Quest): number => {
    if (!userGrade) return 0;

    const questGrades = getQuestGrades(quest);
    if (questGrades.length === 0) return 0;

    // 提取用户年级的数字部分
    const userGradeNum = parseInt(userGrade.replace(/Grade\s*/i, '')) || 0;

    // 找到最匹配的年级分数（取最大值而不是累加）
    let maxScore = 0;
    for (const grade of questGrades) {
      // 提取 quest 年级的数字部分
      const gradeNum = parseInt(grade.replace(/Grade\s*/i, '')) || 0;
      // 年级越接近，分数越高
      const diff = Math.abs(gradeNum - userGradeNum);
      // 当前年级得10分，高一级年级额外加0.5分，其他年级根据距离递减
      const currentScore = diff === 0 ? 10 : 10 - diff + (gradeNum > userGradeNum ? 0.5 : 0);
      maxScore = Math.max(maxScore, currentScore);
    }

    return maxScore;
  };

  if (!questsWithLabels || questsWithLabels.length === 0) {
    return [];
  }

  // 排序
  return questsWithLabels.sort((a, b) => {
    // 1. 书架状态（如果提供 bookshelfQuestIds）：不在书架上的书籍优先
    if (bookshelfQuestIds && bookshelfQuestIds.length > 0) {
      const aInBookshelf = bookshelfQuestIds.includes(a.id);
      const bInBookshelf = bookshelfQuestIds.includes(b.id);
      if (!aInBookshelf && bInBookshelf) return -1;
      if (aInBookshelf && !bInBookshelf) return 1;
    }

    // 2. 锁定状态：未锁定的书籍优先
    if (a.lock && !b.lock) return 1;
    if (!a.lock && b.lock) return -1;

    // 3. 学习进度（如果提供 allProgress）：最近学习的优先
    if (allProgress && allProgress.length > 0) {
      const aProgress = getLatestProgress(a);
      const bProgress = getLatestProgress(b);

      if (aProgress && bProgress) {
        if (aProgress.updated_at && bProgress.updated_at) {
          return aProgress.updated_at > bProgress.updated_at ? -1 : 1;
        }
      } else if (aProgress) {
        return -1;
      } else if (bProgress) {
        return 1;
      }
    }

    // 4. 用户所在学区：命中用户学区的书籍优先
    if (a.label.type === 'user_district' && b.label.type !== 'user_district') return -1;
    if (a.label.type !== 'user_district' && b.label.type === 'user_district') return 1;

    // 5. Premium 学校：Challenger 和 BASIS 学区的书籍次之
    if (a.label.type === 'premium_school' && b.label.type !== 'premium_school') return -1;
    if (a.label.type !== 'premium_school' && b.label.type === 'premium_school') return 1;

    // 6. 系列排序：同一系列的书放在一起，不同系列按字母排序
    const aSeries = a.series || '';
    const bSeries = b.series || '';

    // 没有系列的书排在最后
    if (aSeries && !bSeries) return -1;
    if (!aSeries && bSeries) return 1;

    // 不同系列按字母排序
    if (aSeries !== bSeries) {
      return aSeries.localeCompare(bSeries);
    }

    // 7. 年级匹配（如果提供 userGrade）：同一系列内按年级排序
    if (userGrade) {
      const aMatchScore = getGradeMatchScore(a);
      const bMatchScore = getGradeMatchScore(b);
      if (aMatchScore > bMatchScore) return -1;
      if (aMatchScore < bMatchScore) return 1;
    }

    // 8. 其他书籍保持原有顺序
    return 0;
  });
};

/**
 * 获取 Quest 的 Premium School 展示信息
 * 检查 quest 的 school_district_grade_mapping 是否包含 Challenger 或 BASIS
 * @param quest - Quest 对象
 * @returns 如果是 premium school 返回学校名称数组（直接从数据库字段获取），否则返回 null
 */
export const getPremiumSchoolDisplay = (quest: Quest): string[] | null => {
  if (!quest.school_district_grade_mapping || quest.school_district_grade_mapping.length === 0) {
    return null;
  }

  // 获取所有包含 Challenger 或 BASIS 的 school_district
  const premiumSchools = quest.school_district_grade_mapping
    .filter(mapping => {
      const schoolName = mapping.school_district.toLowerCase();
      return schoolName.includes('challenger') || schoolName.includes('basis');
    })
    .map(mapping => mapping.school_district);

  // 去重
  const uniqueSchools = Array.from(new Set(premiumSchools));

  return uniqueSchools.length > 0 ? uniqueSchools : null;
};

export const onboardPrepareQuests = (quests: Quest[], profile: any, userDistrict: any): QuestWithLabel[] => {
  const sorted = sortQuestsByUserProfile(quests, {
    userDistrict: userDistrict || undefined,
    userGrade: profile?.onboard_data?.profile?.grade || undefined
  });

  const matchedQuests = [];
  const userGrade = normalizeGrade(profile?.onboard_data?.profile?.grade);
  const subjectDefaultGradeMap = new Map<string, string>();
  const matchedSubjects = new Set<string>();

  if (userGrade) {
    subjectDefaultGradeMap.set('math', userGrade);
    subjectDefaultGradeMap.set('science', userGrade);
    subjectDefaultGradeMap.set('history', userGrade);
    subjectDefaultGradeMap.set('vocabulary', userGrade);
    subjectDefaultGradeMap.set('grammar', userGrade);
  }

  // 第一步：尝试匹配学区 + 年级
  for (const quest of sorted) {
    if (quest.label.type === 'user_district') {
      if (matchQuestByDistrictAndGrade(quest, normalizeSchoolDistrict(userDistrict || ''), userGrade || '')) {
        matchedQuests.push(quest);
        subjectDefaultGradeMap.set(quest.subject || '', quest.default_grade || '');
        matchedSubjects.add(quest.subject || '');
      }
    }
  }
  console.log('Step 1 matchedQuests: ' + matchedQuests.length);

  // 第二步：如果不足5本，尝试匹配本学区高一个年级的书
  if (matchedQuests.length < 5 && userGrade) {
    const higherGrade = getNextGrade(userGrade);
    if (higherGrade) {
      for (const quest of sorted) {
        if (!matchedQuests.includes(quest) && matchedQuests.length < 5) {
          if (quest.label.type === 'user_district') {
            if (matchQuestByDistrictAndGrade(quest, normalizeSchoolDistrict(userDistrict || ''), higherGrade || '')) {
              matchedQuests.push(quest);
              matchedSubjects.add(quest.subject || '');
            }
          }
        }
      }
    }
  }
  console.log('Step 2 matchedQuests: ' + matchedQuests.length);
  // 第三步：如果还不足5本，尝试只匹配年级
  if (matchedQuests.length < 5 && userGrade) {

    // 第一轮：优先添加同年级不同学科的书
    for (const quest of sorted) {
      if (!matchedQuests.includes(quest) && matchedQuests.length < 5) {
        if (quest.default_grade === subjectDefaultGradeMap.get(quest.subject || '') && !matchedSubjects.has(quest.subject || '')) {
          matchedQuests.push(quest);
          matchedSubjects.add(quest.subject || '');
        }
      }
    }
    console.log('Step 3.1 matchedQuests: ' + matchedQuests.length);
    // 第二轮：如果还不足5本，添加同年级任何学科的书
    if (matchedQuests.length < 5) {
      for (const quest of sorted) {
        if (!matchedQuests.includes(quest) && matchedQuests.length < 5) {
          if (quest.default_grade === subjectDefaultGradeMap.get(quest.subject || '')) {
            matchedQuests.push(quest);
          }
        }
      }
    }
  }
  console.log('Step 3.2 matchedQuests: ' + matchedQuests.length);
  // 第四步：如果还不足5本，则补齐（优先选择不同学科）
  if (matchedQuests.length < 5) {

    // 第一轮：优先添加不同学科的书
    for (const quest of sorted) {
      if (!matchedQuests.includes(quest) && matchedQuests.length < 5) {
        if (!matchedSubjects.has(quest.subject || '')) {
          matchedQuests.push(quest);
          matchedSubjects.add(quest.subject || '');
        }
      }
    }
    console.log('Step 4.1 matchedQuests: ' + matchedQuests.length);
    // 第二轮：如果还不足5本，添加任何可用的书
    if (matchedQuests.length < 5) {
      for (const quest of sorted) {
        if (!matchedQuests.includes(quest) && matchedQuests.length < 5) {
          matchedQuests.push(quest);
        }
      }
    }
  }
  console.log('Step 4.2 matchedQuests: ' + matchedQuests.length);
  return matchedQuests;
}
// 只导出没有使用 export 关键字导出的函数和变量
export { getWelcomeMessage, transformElement, transformElementType, getQuestModes };